import {
  isArray,
  isEqual,
  isEqualWith,
  isNull,
  isObject,
  isUndefined,
  omit,
} from 'lodash-es'

// Custom loadash comparison function to recursively compare objects
// and to treat empty strings, undefined, and null as equal so that
// the form is not considered dirty when the user resets a field
export const isDirtyCompare = (
  a: { [index: string]: object } & string & string[],
  b: { [index: string]: object } & string & string[]
) => {
  if (isArray(a) && isArray(b)) {
    const aFiltered = a.filter(
      (item) => item !== null && item !== undefined && item !== ''
    )
    const bFiltered = b.filter(
      (item) => item !== null && item !== undefined && item !== ''
    )
    return (
      aFiltered.length === bFiltered.length &&
      aFiltered.every((val: string | object, index: number) =>
        isEqualWith(val, bFiltered[index], isDirtyCompare)
      )
    )
  } else if (isObject(a) && isObject(b)) {
    const keysA = Object.keys(a)
    const keysB = Object.keys(b)
    const isMeaningful = (v: any) =>
      !(
        isNull(v) ||
        isUndefined(v) ||
        v === '' ||
        (isArray(v) && v.length === 0)
      )

    for (const k of keysA) {
      if (!keysB.includes(k) && isMeaningful(a[k])) return false
    }
    for (const k of keysB) {
      if (!keysA.includes(k) && isMeaningful(b[k])) return false
    }

    return keysA.every((key: string) =>
      isEqualWith(a[key], b[key], isDirtyCompare)
    )
  } else if (
    (isNull(a) ||
      isUndefined(a) ||
      a === '' ||
      (isArray(a) && a.length === 0)) &&
    (isNull(b) || isUndefined(b) || b === '' || (isArray(b) && b.length === 0))
  )
    return true

  return isEqual(a, b)
}

export function isDirtyEqual(
  a: any,
  b: any,
  ignorePaths: string[] = []
): boolean {
  const cleanA = omit(a, expandArrayPaths(a, ignorePaths))
  const cleanB = omit(b, expandArrayPaths(b, ignorePaths))
  return isEqualWith(cleanA, cleanB, isDirtyCompare)
}

function expandArrayPaths(obj: any, paths: string[]): string[] {
  const expanded: string[] = []

  for (const path of paths) {
    const parts = path.split('.')
    let current: any = obj
    let prefix = ''

    for (let i = 0; i < parts.length; i++) {
      const part = parts[i]

      if (isArray(current)) {
        // expand into all indexes
        current.forEach((item, idx) => {
          const subPaths = expandArrayPaths(item, [parts.slice(i).join('.')])
          subPaths.forEach((sub) => {
            expanded.push(`${prefix}[${idx}].${sub}`)
          })
        })
        break
      } else {
        prefix = prefix ? `${prefix}.${part}` : part
        current = current?.[part]
        if (i === parts.length - 1) {
          expanded.push(prefix)
        }
      }
    }
  }

  return expanded
}
